import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Space, message, Typography, Form, Input, Select, Col, Upload, Modal } from 'antd';
import { PlusOutlined, ReloadOutlined, CheckCircleOutlined, StopOutlined, UploadOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FixtureTable,
} from './components';
import BatchActionToolbar from '../../components/common/BatchActionToolbar';
import FilterPanel from '../../components/common/FilterPanel';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import { useErrorHandling } from '../../hooks/useErrorHandling';
import fixtureService from '../../services/fixtureService';
import {
  FixtureType,
  FixtureFilters,
  FixturePaginationParams,
  FixtureLoadingState,
} from '../../types/fixture';
import { getFixtureStatusOptions } from '../../utils/fixtureConstants';
import '../../styles/fixture.css';

const FixtureList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { error, setError, clearError } = useErrorHandling();

  // State management
  const [fixtures, setFixtures] = useState<FixtureType[]>([]);
  const [filters, setFilters] = useState<FixtureFilters>({});
  const [pagination, setPagination] = useState<FixturePaginationParams>({
    currentPage: 1,
    pageSize: 20,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState<FixtureLoadingState>({
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
    batchUpdate: false,
  });

  // Import functionality state
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);

  // Load fixtures data
  const loadFixtures = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, list: true }));
      clearError();

      const response = await fixtureService.getFixtures(filters, pagination);
      
      if (response.success && response.data) {
        setFixtures(response.data.tableData);
        setTotalCount(response.data.paging.totalCount);
      } else {
        setError(new Error(response.errorMessage || t('fixture.getFixtureListFailed')));
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(prev => ({ ...prev, list: false }));
    }
  }, [filters, pagination, setError, clearError]);

  // Initial load
  useEffect(() => {
    loadFixtures();
  }, [loadFixtures]);


  const handleReset = () => {
    setFilters({});
    setPagination({ currentPage: 1, pageSize: 20 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      pageSize,
    }));
  };

  const handleSortChange = (field: string, order: 'asc' | 'desc') => {
    setPagination(prev => ({
      ...prev,
      sortBy: field as any,
      sortOrder: order,
      currentPage: 1,
    }));
  };

  const handleView = (id: string) => {
    navigate(`/fixture/detail/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/fixture/edit/${id}`);
  };

  const handleCreate = () => {
    navigate('/fixture/create');
  };

  const handleRefresh = () => {
    loadFixtures();
  };

  const handleSelectionChange = (keys: string[]) => {
    setSelectedRowKeys(keys);
  };

  const handleClearSelection = () => {
    setSelectedRowKeys([]);
  };

  const handleActivate = async (ids: string[]) => {
    try {
      setLoading(prev => ({ ...prev, batchUpdate: true }));
      
      const response = await fixtureService.activateFixtures(ids);
      
      if (response.success) {
        message.success(t('fixture.activateSuccess'));
        loadFixtures();
      } else {
        throw new Error(response.errorMessage || t('fixture.activateFailed'));
      }
    } catch (err) {
      setError(err as Error);
      throw err; // Re-throw to let the component handle it
    } finally {
      setLoading(prev => ({ ...prev, batchUpdate: false }));
    }
  };

  const handleDeactivate = async (ids: string[]) => {
    try {
      setLoading(prev => ({ ...prev, batchUpdate: true }));
      
      const response = await fixtureService.deactivateFixtures(ids);
      
      if (response.success) {
        message.success(t('fixture.deactivateSuccess'));
        loadFixtures();
      } else {
        throw new Error(response.errorMessage || t('fixture.deactivateFailed'));
      }
    } catch (err) {
      setError(err as Error);
      throw err; // Re-throw to let the component handle it
    } finally {
      setLoading(prev => ({ ...prev, batchUpdate: false }));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(prev => ({ ...prev, delete: true }));
      
      const response = await fixtureService.deleteFixture(id);
      
      if (response.success) {
        message.success(t('fixture.deleteSuccess'));
        // Remove the deleted fixture from the list
        setFixtures(prev => prev.filter(fixture => fixture.id !== id));
        // Update total count
        setTotalCount(prev => prev - 1);
        // Remove from selection if it was selected
        setSelectedRowKeys(prev => prev.filter(key => key !== id));
      } else {
        throw new Error(response.errorMessage || t('fixture.deleteFailed'));
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  const selectedFixtures = fixtures.filter(fixture => 
    selectedRowKeys.includes(fixture.id)
  );

  const hasSelected = selectedRowKeys.length > 0;
  const draftCount = selectedFixtures.filter(f => f.status === 'Draft').length;
  const activeCount = selectedFixtures.filter(f => f.status === 'Active').length;
  const inactiveCount = selectedFixtures.filter(f => f.status === 'Inactive').length;

  if (error) {
    return (
      <div className="fixture-list">
        <div className="page-header">
          <Typography.Title level={4}>{t('fixture.listTitle')}</Typography.Title>
        </div>
        <ErrorDisplay
          error={error}
          onRetry={loadFixtures}
        />
      </div>
    );
  }

  // Handle import display config
  const handleImportDisplayConfig = () => {
    setImportModalVisible(true);
  };

  // Handle import file upload
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error(t('fixture.import.selectFileFirst'));
      return;
    }

    const file = fileList[0];
    setImportLoading(true);

    try {
      message.loading(t('fixture.import.uploading'), 0);
      const response = await fixtureService.importFixtureTypes(file);
      
      if (response.success) {
        message.destroy();
        message.success(t('fixture.import.success'));
        setImportModalVisible(false);
        setFileList([]);
        loadFixtures(); // Refresh list

        // 如果有返回文件，提供下载
        if (response.data) {
          const link = document.createElement('a');
          link.href = response.data.downloadUrl;
          link.download = response.data.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        message.destroy();
        message.error(response.errorMessage || t('fixture.import.failed'));
      }
    } catch (err) {
      message.destroy();
      console.error('Import error:', err);
      message.error(err.message || t('fixture.import.failed'));
    } finally {
      setImportLoading(false);
    }
  };

  // Handle import modal cancel
  const handleImportCancel = () => {
    setImportModalVisible(false);
    setFileList([]);
  };

  // Upload props
  const uploadProps = {
    accept: '.xlsx,.xls',
    fileList,
    beforeUpload: (file: any) => {
      // Validate file type
      const isValidType = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      if (!isValidType) {
        message.error(t('fixture.import.invalidFileType'));
        return false;
      }
      
      // Validate file size (10MB)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t('fixture.import.fileTooLarge'));
        return false;
      }
      
      setFileList([file]);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <div>

      <FilterPanel
        onFilter={(values) => {
          const newFilters = {
            typeCode: values.typeCode?.trim() || undefined,
            typeName: values.typeName?.trim() || undefined,
            status: values.status === 'all' ? undefined : values.status,
          };
          setFilters(newFilters);
          setPagination(prev => ({ ...prev, currentPage: 1 }));
        }}
        onReset={handleReset}
        // initialValues={{
        //   typeCode: filters.typeCode || '',
        //   typeName: filters.typeName || '',
        //   status: filters.status || 'all',
        // }}
        title={t('menu.fixture')}
      >
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="typeCode" label={t('fixture.searchCode')}>
            <Input placeholder={t('fixture.searchCodePlaceholder')} allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="typeName" label={t('fixture.searchName')}>
            <Input placeholder={t('fixture.searchNamePlaceholder')} allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="status" label={t('fixture.status')}>
            <Select placeholder={t('common.pleaseSelect')} allowClear>
              {getFixtureStatusOptions(t).map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <BatchActionToolbar
            selectedCount={selectedRowKeys.length}
            actions={[
              {
                key: 'activate',
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                label: t('fixture.batchActivate'),
                onClick: () => {
                  if (draftCount === 0 && inactiveCount === 0) {
                    message.warning(t('fixture.noActivatableFixtures'));
                    return;
                  }
                  const draftIds = selectedFixtures
                    .filter(f => f.status === 'Draft' || f.status === 'Inactive')
                    .map(f => f.id);
                  handleActivate(draftIds);
                },
                disabled: (draftCount === 0 && inactiveCount === 0) || loading.batchUpdate,
              },
              {
                key: 'deactivate',
                icon: <StopOutlined style={{ color: '#faad14' }} />,
                label: t('fixture.batchDeactivate'),
                onClick: () => {
                  if (activeCount === 0) {
                    message.warning(t('fixture.noDeactivatableFixtures'));
                    return;
                  }
                  const activeIds = selectedFixtures
                    .filter(f => f.status === 'Active')
                    .map(f => f.id);
                  handleDeactivate(activeIds);
                },
                disabled: activeCount === 0 || loading.batchUpdate,
                danger: true,
              }
            ]}
            onClearSelection={handleClearSelection}
          />
          <Space>
            <Button
              icon={<UploadOutlined />}
              onClick={handleImportDisplayConfig}
            >
              {t('fixture.import.title')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('fixture.add')}
            </Button>
          </Space>
        </div>

        <FixtureTable
          data={fixtures}
          pagination={{
            currentPage: pagination.currentPage,
            pageSize: pagination.pageSize,
            totalCount,
          }}
          loading={loading.list}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
        />
      </Card>

      {/* Import Dialog */}
      <Modal
        title={t('fixture.import.title')}
        open={importModalVisible}
        onOk={handleUpload}
        onCancel={handleImportCancel}
        confirmLoading={importLoading}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>{t('fixture.import.description')}</p>
          <p style={{ color: '#666', fontSize: '12px' }}>
            {t('fixture.import.supportedFormats')}
          </p>
        </div>
        
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('fixture.import.dragText')}</p>
          <p className="ant-upload-hint">
            {t('fixture.import.hint')}
          </p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default FixtureList;